import { useState } from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/empty-state';
import { useDeviceDetails } from '@/hooks/useDeviceDetails';
import { type PortProtectionFacts, portShutBy, portShutDetail } from '@/lib/portProtection';
import {
  type SecurityMode,
  useSetDeviceDefaultSecurityMode,
  useSetInterfaceSecurityMode,
} from '@/hooks/useSecurityPolicy';
import SecurityModeSelector from './SecurityModeSelector';
import InterfaceAllowlistModal from './InterfaceAllowlistModal';
import {
  ResponsiveTable,
  type ResponsiveTableColumn,
} from '@/components/ui/responsive-table';

interface DeviceDefaultSelectorProps {
  deviceId: string;
  initialValue: string | null;
}

function DeviceDefaultSelector({ deviceId, initialValue }: DeviceDefaultSelectorProps) {
  const [value, setValue] = useState<SecurityMode | null>(
    initialValue ? (initialValue as SecurityMode) : null,
  );
  const setMutation = useSetDeviceDefaultSecurityMode(deviceId);

  // Follow the server value when it changes under us (render-time, not an effect).
  const [syncedInitial, setSyncedInitial] = useState(initialValue);
  if (initialValue !== syncedInitial) {
    setSyncedInitial(initialValue);
    setValue(initialValue ? (initialValue as SecurityMode) : null);
  }

  const handleChange = (newMode: SecurityMode | null) => {
    setValue(newMode);
    setMutation.mutate(newMode);
  };

  return (
    <div className="flex flex-wrap items-center gap-2 mb-4">
      <span className="text-sm font-medium">Default mode for this device&apos;s ports:</span>
      <SecurityModeSelector value={value} onChange={handleChange} allowInherit />
      {setMutation.isPending ? (
        <span className="text-xs text-muted-foreground">Saving…</span>
      ) : null}
      {setMutation.isError ? (
        <span className="text-xs text-destructive">Save failed.</span>
      ) : null}
    </div>
  );
}

interface DeviceInterface extends PortProtectionFacts {
  id: string;
  interfaceName: string;
  adminStatus: string | null;
  operStatus: string | null;
}

interface PortRow extends DeviceInterface {
  securityMode: string | null;
}

function InterfaceModeCell({
  interfaceId,
  securityMode,
}: {
  interfaceId: string;
  securityMode: string | null;
}) {
  const [value, setValue] = useState<SecurityMode | null>(
    securityMode ? (securityMode as SecurityMode) : null,
  );
  const setMutation = useSetInterfaceSecurityMode(interfaceId);

  const [syncedMode, setSyncedMode] = useState(securityMode);
  if (securityMode !== syncedMode) {
    setSyncedMode(securityMode);
    setValue(securityMode ? (securityMode as SecurityMode) : null);
  }

  const handleChange = (newMode: SecurityMode | null) => {
    setValue(newMode);
    setMutation.mutate(newMode);
  };

  return (
    <div>
      <SecurityModeSelector value={value} onChange={handleChange} allowInherit />
      {setMutation.isError ? (
        <p className="text-xs text-destructive mt-1">Save failed.</p>
      ) : null}
    </div>
  );
}

interface Props {
  deviceId: string;
}

/**
 * DevicePortsTab — per-port security mode picker + allowlist
 * launcher. Embedded in DeviceDetails > Ports tab.
 *
 * Each row is its own component because mutation state needs to be
 * local to the interface — multiple ports can save independently.
 */
export default function DevicePortsTab({ deviceId }: Props) {
  const { data, isLoading, error } = useDeviceDetails({ deviceId, includeInterfaces: true });
  const [allowlistFor, setAllowlistFor] = useState<{ id: string; name: string } | null>(null);

  if (isLoading) {
    return <Skeleton className="h-64 w-full" />;
  }
  if (error || !data) {
    return (
      <Alert variant="destructive">
        <AlertDescription>Failed to load device.</AlertDescription>
      </Alert>
    );
  }

  const interfaces: PortRow[] = (data.interfaces ?? []).map((iface) => ({
    ...iface,
    securityMode:
      (iface as unknown as { securityMode?: string | null }).securityMode ?? null,
  }));

  const columns: readonly ResponsiveTableColumn<PortRow>[] = [
    {
      key: 'name',
      label: 'Interface',
      hideMobileLabel: true,
      render: (p) => <span className="font-mono font-medium">{p.interfaceName}</span>,
    },
    {
      key: 'admin',
      label: 'Admin',
      render: (p) => p.adminStatus ?? <span className="text-muted-foreground">—</span>,
      hideAt: 'mobile',
    },
    {
      key: 'oper',
      label: 'Oper',
      render: (p) => {
        // A port the device shut reads "down" like an unplugged one; say who shut it.
        const shutBy = portShutBy(p);
        if (shutBy) {
          return (
            <span className="text-destructive font-medium" title={portShutDetail(p) ?? undefined}>
              {p.operStatus ?? 'down'} · {shutBy}
            </span>
          );
        }
        return p.operStatus ?? <span className="text-muted-foreground">—</span>;
      },
      hideAt: 'mobile',
    },
    {
      key: 'mode',
      label: 'Security mode',
      render: (p) => (
        <InterfaceModeCell interfaceId={p.id} securityMode={p.securityMode} />
      ),
    },
    {
      key: 'allowlist',
      label: 'Allowlist',
      render: (p) => {
        const isStrict = p.securityMode === 'strict';
        return (
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setAllowlistFor({ id: p.id, name: p.interfaceName });
            }}
            disabled={!isStrict}
            title={isStrict ? '' : 'Switch the port to "strict" to manage its allowlist.'}
          >
            Manage
          </Button>
        );
      },
    },
  ];

  return (
    <section className="bg-card border border-border rounded-lg p-5">
      <h2 className="text-sm font-semibold mb-1">Port security</h2>
      <p className="text-sm text-muted-foreground mb-4">
        Per-port <code className="font-mono">security_mode</code> overrides. Ports left set
        to <em>(inherit)</em> use the device default, then the region default, then the org
        default.
      </p>

      <DeviceDefaultSelector
        deviceId={deviceId}
        initialValue={
          (data as unknown as { defaultPortSecurityMode?: string | null })
            .defaultPortSecurityMode ?? null
        }
      />

      {interfaces.length === 0 ? (
        <EmptyState
          variant="no-data"
          title="No known interfaces"
          description="This device has no interfaces discovered yet."
          size="inline"
        />
      ) : (
        <ResponsiveTable<PortRow>
          columns={columns}
          rows={interfaces}
          rowKey={(r) => r.id}
        />
      )}

      {allowlistFor ? (
        <InterfaceAllowlistModal
          interfaceId={allowlistFor.id}
          interfaceName={allowlistFor.name}
          onClose={() => { setAllowlistFor(null); }}
        />
      ) : null}
    </section>
  );
}
